// Reads the QR links printed on asset labels and QEW bag tags.
// Approved in STEP3_SIGNIN_APPROVED.md, "Label reader tests".
//
//   Asset label:  https://getsafetag.com/?aid=10-UTL-03
//   Bag tag:      https://getsafetag.com/?tag=00-QEW17
//   Admin tag:    https://getsafetag.com/?tag=ALL-ADMIN-001
//
// Admin tags approved 2026-09-29 in STEP2_ADMIN_SIGNIN_APPROVED.md.
// One entry point for everyone. The ID is the fork: a QEW ID goes to the
// name check, an admin ID opens the PIN. isAdminBadgeId tells them apart.
//
// Hung labels and tags carry no company. They read as Alltech, permanently.
// The company part of new links is not decided yet. When it is, it is read
// here and passed to resolveCompany. Nothing else changes.

import { resolveCompany, siteFromAssetId } from "./company";

export type QrLinkResult =
  | { kind: "asset"; company: string; site: string; assetId: string }
  | { kind: "badge"; company: string; site: string; badgeId: string }
  | { kind: "refused"; message: string };

/**
 * Asset ID as printed. Two-digit site, then dash-separated letters and numbers.
 * Case is kept as printed: 01-ER1-PNL-01-A2a and 01-ER1-PNL-01-A2A are different assets.
 * All 971 asset IDs in the 2026-09-27 registry export fit this pattern.
 */
const ASSET_ID = /^\d{2}(-[A-Za-z0-9]+)+$/;

/**
 * Badge ID as printed on the bag tag: site, QEW or CON, two-digit number.
 * 00-QEW17, 01-QEW01, 99-CON04.
 */
const BADGE_ID = /^(\d{2})-(QEW|CON)(\d{2})$/;

/**
 * Admin badge ID: ALL (every site), ADMIN, three digits numbered per company.
 * ALL-ADMIN-001. Same format as the live app.
 */
const ADMIN_BADGE_ID = /^ALL-ADMIN-\d{3}$/;

/**
 * Cleans up a badge ID from a scan or the typed fallback.
 * Accepts lower case, stray spaces, and a missing or extra dash.
 * Returns null for anything else. It never guesses: "00-QEW)3" is refused.
 */
export function normalizeBadgeId(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const compact = raw.toUpperCase().replace(/\s+/g, "");
  const qew = /^(\d{2})-?(QEW|CON)-?(\d{2})$/.exec(compact);
  if (qew) return `${qew[1]}-${qew[2]}${qew[3]}`;
  const admin = /^ALL-?ADMIN-?(\d{3})$/.exec(compact);
  if (admin) return `ALL-ADMIN-${admin[1]}`;
  return null;
}

/** True when the text is a badge ID exactly as the app stores it. */
export function isBadgeId(value: string): boolean {
  return BADGE_ID.test(value) || ADMIN_BADGE_ID.test(value);
}

/** True when the text is an admin badge ID exactly as the app stores it. Opens the PIN, not the name check. */
export function isAdminBadgeId(value: string): boolean {
  return ADMIN_BADGE_ID.test(value);
}

/** Cleans up an asset ID. Trims only. Returns null when it does not fit the pattern. */
export function normalizeAssetId(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim() ?? "";
  return ASSET_ID.test(trimmed) ? trimmed : null;
}

/** Reads one scanned link. Refuses anything it cannot read exactly. */
export function readQrLink(link: string): QrLinkResult {
  let url: URL;
  try {
    url = new URL(link.trim());
  } catch {
    return { kind: "refused", message: "This is not a SafeTag link." };
  }

  const aid = url.searchParams.get("aid");
  const tag = url.searchParams.get("tag");
  const company = resolveCompany(null);

  if (aid !== null && tag !== null) {
    return { kind: "refused", message: "This link names both an asset and a badge. Scan the label again." };
  }

  if (aid !== null) {
    const assetId = normalizeAssetId(aid);
    const site = assetId ? siteFromAssetId(assetId) : null;
    if (!assetId || !site) {
      return { kind: "refused", message: `"${aid}" is not a valid asset ID.` };
    }
    return { kind: "asset", company, site, assetId };
  }

  if (tag !== null) {
    const badgeId = normalizeBadgeId(tag);
    if (!badgeId) {
      return { kind: "refused", message: `"${tag}" is not a valid badge ID.` };
    }
    // An admin badge covers every site. A QEW badge's site is its first two digits.
    const site = isAdminBadgeId(badgeId) ? "ALL" : badgeId.slice(0, 2);
    return { kind: "badge", company, site, badgeId };
  }

  return { kind: "refused", message: "This link has no asset or badge on it." };
}
